import { atom, AtomEffect, RecoilState } from 'recoil';
import { areCitiesEquivalent } from '../../config/cityData';

export interface LocationState {
  userLocation: any;
}

// In-app browsers and private-mode Safari can throw on sessionStorage writes. The
// mirror keeps reads consistent with what we last wrote, so a rejected write can
// never hand a stale location back to a caller that just cleared it.
let memoryLocation: LocationState | null = null;

// Helper functions for sessionStorage
const getStoredLocation = (): LocationState | null => {
  if (typeof window === 'undefined') return null;
  if (memoryLocation) return memoryLocation;
  try {
    const stored = sessionStorage.getItem('locationState');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setStoredLocation = (value: LocationState) => {
  if (typeof window === 'undefined') return;
  memoryLocation = value;
  try {
    sessionStorage.setItem('locationState', JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

// IMPORTANT: Always use the same default value for SSR to avoid hydration mismatch
// The stored value will be loaded via effects_UNSTABLE after hydration
const DEFAULT_LOCATION: LocationState = { userLocation: null };
const ATOM_KEY = 'locationState';

// Cache for atoms to prevent duplicate registration during HMR
const atomCache: Map<string, RecoilState<any>> = 
  typeof globalThis !== 'undefined' 
    ? ((globalThis as any).__RECOIL_ATOM_CACHE__ ??= new Map())
    : new Map();

// Get or create atom (prevents duplicate key errors during HMR)
const getOrCreateAtom = <T>(
  key: string,
  config: { default: T; effects_UNSTABLE?: AtomEffect<T>[] }
): RecoilState<T> => {
  if (!atomCache.has(key)) {
    atomCache.set(key, atom<T>({ key, ...config }));
  }
  return atomCache.get(key)!;
};

// Location state effect
const locationEffect: AtomEffect<LocationState> = ({ setSelf, onSet, trigger }) => {
  // Load stored value on client after hydration
  if (trigger === 'get' && typeof window !== 'undefined') {
    const stored = getStoredLocation();
    if (stored?.userLocation) {
      // Validate: reset if stored location's city doesn't match selected city
      const selectedCity = localStorage.getItem('selectedCity');
      const locationCity = stored.userLocation?.city;
      if (selectedCity && locationCity && !areCitiesEquivalent(locationCity, selectedCity)) {
        setSelf({ userLocation: null });
        setStoredLocation({ userLocation: null });
      } else {
        setSelf(stored);
      }
    } else if (stored) {
      setSelf(stored);
    }
  }
  
  // Save to sessionStorage when value changes
  onSet((newValue) => {
    setStoredLocation(newValue);
  });
};

export const locationState = getOrCreateAtom<LocationState>(ATOM_KEY, {
  default: DEFAULT_LOCATION,
  effects_UNSTABLE: [locationEffect],
});
